import { Col, DatePicker, Form, FormInstance, Input, Modal, Row, Select } from 'antd';
import React, { Component } from 'react';
import { FormattedMessage, injectIntl, WrappedComponentProps } from 'react-intl';
import scheduleApi from '../../../../../../api/ScheduleApi';
import CustomContext from '../../../../../../context/CustomContext';
import { Connection, Schedule } from '../../../../../../model/entities';
import { frequencies } from '../../../../../../model/types';
import notificationService from '../../../../../../services/NotificationService';

/**
 * This component represents a connection schedule modal.
 * @param props - the props
 */
class ConnectionScheduleModal extends Component<Props, State> {
    static contextType = CustomContext;
    context!: React.ContextType<typeof CustomContext>;
    formRef = React.createRef<FormInstance>();

    constructor(props: Props) {
        super(props);
        this.state = {};
    }

    componentDidMount() {
        this.init();
    }

    /** METHODS **/

    init = async () => {
        try {
            this.setState({ loading: true });
            if (this.props.scheduleId) {
                await this.get(this.props.scheduleId);
            } else {
                await this.new();
            }
        } catch (error) {
            notificationService.displayError(error, this.props.intl);
        } finally {
            this.setState({ loading: undefined });
        }
    };

    new = async () => {
        const { connection } = this.props;
        const schedule: Schedule = { connectionId: connection.id };
        this.setState({ schedule });
        this.formRef.current!.setFieldsValue(schedule);
    };

    get = async (id: number) => {
        // get schedule
        const schedule = await scheduleApi.get(id);
        this.setState({ schedule });

        // load form values
        this.formRef.current!.setFieldsValue(schedule);
    };

    cancel = () => {
        this.formRef.current!.resetFields();
        this.props.onCancel();
    };

    submit = async () => {
        try {
            const values = await this.formRef.current!.validateFields();
            await this.save(values);
        } catch (error) {}
    };

    save = async (values: any) => {
        try {
            this.setState({ saving: true });
            const schedule = Object.assign({}, this.state.schedule, values);
            schedule.id ? await scheduleApi.update(schedule) : await scheduleApi.create(schedule);
            this.formRef.current!.resetFields();
            this.props.onSave();
        } catch (error) {
            notificationService.displayError(error, this.props.intl, [
                { status: 409, message: 'schedule.status.duplicate' },
            ]);
        } finally {
            this.setState({ saving: false });
        }
    };

    /*** COMPONENTS ***/

    render() {
        const { visible } = this.props;
        const { saving } = this.state;
        const frequencyOptions = frequencies.map((frequency) => (
            <Select.Option key={frequency} value={frequency}>
                <FormattedMessage id={frequency} />
            </Select.Option>
        ));

        return (
            <Modal
                title={<FormattedMessage id="schedule.modal.title" />}
                visible={visible}
                onCancel={this.cancel}
                onOk={this.submit}
                okButtonProps={{ loading: saving }}
                cancelButtonProps={{ disabled: saving }}
            >
                <Form ref={this.formRef} colon={false} layout="vertical">
                    <Row gutter={[28, 0]}>
                        <Col span={24}>
                            <Form.Item
                                label={<FormattedMessage id="schedule.name" />}
                                name="name"
                                rules={[{ required: true, message: <FormattedMessage id="status.mandatory" /> }]}
                            >
                                <Input maxLength={200} size="large" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={[28, 0]}>
                        <Col span={12}>
                            <Form.Item
                                label={<FormattedMessage id="schedule.started" />}
                                name="started"
                                rules={[{ required: true, message: <FormattedMessage id="status.mandatory" /> }]}
                            >
                                <DatePicker size="large" showTime={{ format: 'HH:mm' }} format="DD/MM/YYYY HH:mm" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item label={<FormattedMessage id="schedule.frequency" />} name="frequency">
                                <Select size="large" allowClear>
                                    {frequencyOptions}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Modal>
        );
    }
}
export default injectIntl(ConnectionScheduleModal);

interface Props extends WrappedComponentProps {
    visible?: boolean;
    connection: Connection;
    scheduleId?: number;
    onSave: () => Promise<void>;
    onCancel: () => void;
}

interface State {
    schedule?: Schedule;
    saving?: boolean;
    loading?: boolean;
}
